/**
 * 主题色阶派生。
 *
 * 背景：主题切换原本只更新了 --primary-color 一个变量，
 * 而 --primary-light / --primary-dark 是在 :root 里写死的绿色（#81C784 / #388E3C），
 * 从不随主题变化。于是用户把主题改成「温暖橙」之后：
 *
 *   .time-tag 等组件 = 浅绿底(#81C784，写死) + 橙色边框(--primary-color，已跟随)
 *
 * 一个组件里同时出现绿和橙 —— 这就是「绿橙配色」的来源，不是设计意图。
 *
 * 这里改成从单一主题色推导出整套色阶，写入 CSS 变量。
 * 好处是任何主题（绿/橙/蓝/紫）都自洽，不会再出现两套色系打架。
 */

/** 兜底主题色，与 :root 的 --primary-color 保持一致 */
export const DEFAULT_THEME = '#4CAF50';

/** 解析 #RGB / #RRGGBB，返回 {r,g,b}；无法解析时返回 null */
export function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  const s = hex.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    return {
      r: parseInt(s[0] + s[0], 16),
      g: parseInt(s[1] + s[1], 16),
      b: parseInt(s[2] + s[2], 16),
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(s)) {
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
    };
  }
  return null;
}

const toHexPair = n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');

/**
 * 提亮（t > 0）或加深（t < 0）颜色，t ∈ [-1, 1]。
 *
 * 做法是往白/黑方向线性插值，而不是直接调 HSL 亮度 ——
 * 后者在浅色主题下容易把颜色调灰，插值法更可控。
 */
export function shade(hex, t) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const target = t >= 0 ? 255 : 0;
  const k = Math.abs(t);
  const mix = c => Math.round(c + (target - c) * k);
  return `#${toHexPair(mix(rgb.r))}${toHexPair(mix(rgb.g))}${toHexPair(mix(rgb.b))}`;
}

/** 转成 rgba() 字符串，用于半透明底与阴影 */
export function withAlpha(hex, alpha) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * 相对亮度（WCAG 定义）：先做 sRGB 线性化，再按人眼敏感度加权。
 * 必须线性化，直接用 RGB 平均值算出来的对比度是错的。
 */
export function relativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const lin = c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** 两个颜色的对比度，返回 1 ~ 21 的比值 */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** 白底上正文可读的最低对比度（WCAG 2.1 AA） */
export const MIN_TEXT_CONTRAST = 4.5;

/**
 * 求出「在白底上刚好够读」的深色档。
 *
 * 为什么不能用一个固定加深系数：不同主题色的起始亮度差别很大 ——
 * 深紫加深 24% 对比度已经到 9:1，而橙色加深 24% 只有 3.9:1（低于 AA 标准）。
 * 固定系数要么让深色主题过暗、要么让亮色主题不达标，所以这里按目标对比度反推。
 *
 * 起点取 0.24：默认绿主题在此处已经达标（4.6:1），于是默认主题外观与改动前一致，
 * 只有橙色这类偏亮的主题才会被继续加深。
 */
export function readableDarkShade(hex, { start = 0.24, step = 0.02, max = 0.6 } = {}) {
  const base = parseHex(hex) ? hex : DEFAULT_THEME;
  let t = start;
  let current = shade(base, -t);
  while (t < max && contrastRatio(current, '#FFFFFF') < MIN_TEXT_CONTRAST) {
    t += step;
    current = shade(base, -t);
  }
  return current;
}

/**
 * 由主题色派生整套 CSS 变量。
 *
 * 浅色档用于背景与深色模式下的文字（要够亮），
 * 深色档用于浅色模式下的文字（要够深，且必须过无障碍对比度门槛）。
 */
export function buildThemeScale(themeColor) {
  const base = parseHex(themeColor) ? themeColor : DEFAULT_THEME;
  return {
    '--primary-color': base,
    '--primary-light': shade(base, 0.28),
    '--primary-dark': readableDarkShade(base),
    '--primary-soft': withAlpha(base, 0.1),
    '--primary-tint': withAlpha(base, 0.18),
    '--primary-shadow': withAlpha(base, 0.28),
  };
}

/** 把色阶写进 CSS 变量（唯一与 DOM 打交道的地方，便于其余部分单测） */
export function applyThemeScale(themeColor, root = document.documentElement) {
  const scale = buildThemeScale(themeColor);
  for (const [key, value] of Object.entries(scale)) {
    root.style.setProperty(key, value);
  }
  return scale;
}
